import React, { memo, useReducer } from 'react';
import { View, ScrollView } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  Space,
  IconButton,
  IconBack,
  Divider,
  Text,
  LoadingContainer,
} from '@redlink/components/atoms';
import {
  ProductBasketItem,
  TextInputPreview,
  Button, useAlert,
} from '@redlink/components/molecules';
import {
  useMutation,
  useQuery,
  getProductBasketItems,
  newOrderQuery,
  resetProductBasket,
} from '@redlink/services';
import { CompatibleContainer } from '@redlink/components/pages/previewDashboard/compatibleContainer';
import { useLocale } from '@redlink/assets/locale';
import { useMoneyMask } from '@redlink/core';
import { initialState, reducer, types } from './store';

export const ProductBasketPage = memo(({ navigation, route }) => {
  const { styles, colors } = useStyle();
  const { dictionary } = useLocale();
  const { getValue } = useMoneyMask();
  const alert = useAlert();
  const { pageName } = route.params;

  const {
    data: { ProductBasketItems: data },
  } = useQuery(getProductBasketItems);

  const [{ contact, description }, dispatch] = useReducer(
    reducer,
    initialState
  );

  const [newOrder, { loading }] = useMutation(newOrderQuery, {
    done: () => {
      navigation.goBack();
      alert.show(dictionary.newOrderSuccess, { type: 'info' });
      setTimeout(() => {
        resetProductBasket();
      }, 500);
    },
  });

  const totalPrice = data.reduce(
    (prev, { count, item: { price, discount } }) =>
      prev + (price - discount) * count,
    0
  );
  const totalPriceText = `${getValue(totalPrice)} ${dictionary.moneyPostfix}`;
  const basketItemsCount = data.filter(({ count }) => count > 0).length;

  return (
    <CompatibleContainer>
      <LoadingContainer isBusy={loading} style={styles.container}>
        <View style={styles.headerContainer}>
          <Text style={styles.headerText}>{dictionary.productBasket}</Text>
          <IconButton
            style={styles.backIconContainer}
            onPress={() => navigation.goBack()}
          >
            <IconBack color={colors.gray23} />
          </IconButton>
        </View>
        <ScrollView
          contentContainerStyle={styles.scrollContentContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.bodyContainer}>
            {data
              .map((item, index) => <ProductBasketItem {...{ item }} />)
              .reduce(
                (prev, current) => [
                  prev,
                  current,
                  <Divider margin={15} color={colors.saparator} />,
                ],
                []
              )}
          </View>
          <Space size={24} />
          <TextInputPreview
            require
            title={dictionary.contact}
            value={contact}
            onChangeText={(value) =>
              dispatch({ type: types.SET_CONTACT, payload: value })
            }
          />
          <Space size={8} />
          <Text style={styles.contactTipText}>{dictionary.contactTip}</Text>
          <Space size={16} />
          <TextInputPreview
            multiline
            title={dictionary.description}
            value={description}
            onChangeText={(value) =>
              dispatch({ type: types.SET_DESCRIPTION, payload: value })
            }
          />
        </ScrollView>
        <View style={styles.footerContainer}>
          <View style={styles.totalPriceContainer}>
            <Text style={styles.totalPriceTitleText}>
              {dictionary.totalPrice}
            </Text>
            <Text style={styles.totalPriceValueText}>{totalPriceText}</Text>
          </View>
          <Space size={34} />
          <Button
            style={styles.submitButton}
            variant="green"
            title={dictionary.submitOrder}
            disabled={basketItemsCount === 0}
            onPress={() => {
              newOrder({
                contact,
                description,
                pageName,
                items: data.map(({ item: { id: product }, count }) => ({
                  product,
                  count,
                })),
              });
            }}
          />
        </View>
      </LoadingContainer>
    </CompatibleContainer>
  );
});

const useStyle = createUseStyle(({ colors, size, isRtl }) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContentContainer: {
    padding: 16,
  },
  bodyContainer: {},
  headerContainer: {
    paddingHorizontal: 16,
    justifyContent: 'center',
    backgroundColor: colors.green1,
    height: 56,
    alignItems: 'flex-end',
  },
  headerText: {
    textAlign: 'center',
    position: 'absolute',
    left: 0,
    right: 0,
    color: colors.white,
    fontSize: 16,
  },
  footerContainer: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  totalPriceTitleText: {
    fontSize: 14,
    color: colors.subtitle,
  },
  totalPriceValueText: {
    fontSize: 16,
    color: colors.title,
  },
  submitButton: {
    flex: 1,
    height: 48,
  },
  contactTipText: {
    color: colors.subtitle,
  },
  backIconContainer: {
    backgroundColor: colors.gray7,
    position: 'absolute',
    ...(isRtl && {right: 16}),
    ...(!isRtl && {left: 16}),
  }
}));
